import { z } from 'zod';

export const enrichmentItemZod = z.object({
  componentId: z.string().nullable().optional(),
  value: z.unknown(),
});

export const dynamicEnrichmentEntryZod = z.object({
  providerId: z.string(),
  targetKey: z.string(),
  items: z.array(enrichmentItemZod),
});

export const skippedProviderZod = z.object({
  providerId: z.string(),
  kind: z.enum(['ERROR', 'TIMEOUT']),
  reason: z.string(),
});

export const dynamicEnrichmentResponseZod = z.object({
  entries: z.array(dynamicEnrichmentEntryZod),
  skippedProviders: z.array(skippedProviderZod),
});

export const dynamicEnrichmentScopeFilterZod = z.object({
  systemTypes: z.array(z.string()).min(1).optional(),
  componentTypes: z.array(z.string()).min(1).optional(),
});

export const updateDynamicEnrichmentProviderRequestZod = z
  .object({
    enabled: z.boolean().optional(),
    failureMode: z.enum(['FAIL_OPEN', 'FAIL_CLOSED']).optional(),
    timeoutSeconds: z.number().int().positive().optional(),
    cacheTtlSeconds: z.number().int().nonnegative().optional(),
    scopeFilter: dynamicEnrichmentScopeFilterZod.nullable().optional(),
  })
  .strict();

export const dynamicEnrichmentProviderZod = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  source: z.enum(['PLATFORM', 'CUSTOMER']),
  providerType: z.enum(['INTERNAL', 'HTTP']),
  targetKey: z.string(),
  outputGranularity: z.enum(['PRODUCT', 'COMPONENT']),
  enabled: z.boolean(),
  failureMode: z.enum(['FAIL_OPEN', 'FAIL_CLOSED']),
  timeoutSeconds: z.number().int().positive(),
  cacheTtlSeconds: z.number().int().nonnegative(),
  scopeFilter: dynamicEnrichmentScopeFilterZod.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const listDynamicEnrichmentProvidersRequestZod = z.object({
  source: z.enum(['PLATFORM', 'CUSTOMER']).optional(),
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().min(1).max(100).default(20),
});

export const paginatedDynamicEnrichmentProvidersZod = z.object({
  items: z.array(dynamicEnrichmentProviderZod),
  total: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
  limit: z.number().int().min(1),
});

export type EnrichmentItem = z.infer<typeof enrichmentItemZod>;
export type DynamicEnrichmentEntry = z.infer<typeof dynamicEnrichmentEntryZod>;
export type SkippedProvider = z.infer<typeof skippedProviderZod>;
export type DynamicEnrichmentResponse = z.infer<
  typeof dynamicEnrichmentResponseZod
>;
export type DynamicEnrichmentScopeFilter = z.infer<
  typeof dynamicEnrichmentScopeFilterZod
>;
export type UpdateDynamicEnrichmentProviderRequest = z.infer<
  typeof updateDynamicEnrichmentProviderRequestZod
>;
export type DynamicEnrichmentProvider = z.infer<
  typeof dynamicEnrichmentProviderZod
>;
export type ListDynamicEnrichmentProvidersRequest = z.infer<
  typeof listDynamicEnrichmentProvidersRequestZod
>;
export type PaginatedDynamicEnrichmentProviders = z.infer<
  typeof paginatedDynamicEnrichmentProvidersZod
>;
